/**
 * Voice AI — Agent Page (Orchestrator)
 * Thin shell that owns top-level state and delegates rendering
 * to sub-components: agent-grid, agent-detail.
 * Dispatches: (none — top-level page component)
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { agentStyles } from '../../styles/agent-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { agentService } from '../../services/agent-service.js';
import { AbortableLoader } from '../../services/abortable-loader.js';
import { llmService } from '../../services/llm-service.js';
import { speechService } from '../../services/speech-service.js';

import './agent-grid.js';
import './agent-detail.js';
import '../voice-modal.js';

export class AgentPage extends LitElement {
  static properties = {
    agents: { type: Array },
    selectedAgent: { type: Object },
    _loading: { type: Boolean, state: true },
    _searchQuery: { type: String, state: true },
    _statusFilter: { type: String, state: true },
    _toastVisible: { type: Boolean, state: true },
    _llmProviders: { type: Array, state: true },
    _sttProviders: { type: Array, state: true },
    _ttsProviders: { type: Array, state: true },
    _contextualLlmProviders: { type: Array, state: true },
    _showCreateModal: { type: Boolean, state: true },
    _createName: { type: String, state: true },
  };

  static styles = [
    designSystem,
    componentStyles,
    agentStyles,
    css`
      :host { display: block; }
      .toast {
        position: fixed;
        bottom: var(--space-6);
        right: var(--space-6);
        background: var(--color-success);
        color: var(--color-text-inverse);
        padding: var(--space-3) var(--space-5);
        border-radius: var(--radius-lg);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        display: flex;
        align-items: center;
        gap: var(--space-2);
        box-shadow: var(--shadow-xl);
        z-index: var(--z-toast);
        transform: translateY(100px);
        opacity: 0;
        transition: all var(--duration-slow) var(--ease-out);
      }
      .toast.visible { transform: translateY(0); opacity: 1; }
    `,
  ];

  constructor() {
    super();
    this.agents = [];
    this.selectedAgent = null;
    this._loading = true;
    this._searchQuery = '';
    this._statusFilter = '';
    this._toastVisible = false;
    this._toastMessage = '';
    this._llmProviders = [];
    this._sttProviders = [];
    this._ttsProviders = [];
    this._contextualLlmProviders = [];
    this._showCreateModal = false;
    this._createName = '';
    this._loader = new AbortableLoader();
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadAgents();
    this._loadProviderLists();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._loader.abort();
  }

  /* ─── Data Loading ─── */

  async _loadAgents() {
    this._loading = true;

    const result = await this._loader.run(signal =>
      agentService.listAgents({
        search: this._searchQuery,
        status: this._statusFilter,
      }, { signal })
    );

    if (result.aborted) return;

    if (result.error) {
      console.warn('[Agents] API unavailable:', result.error.message);
      this.agents = [];
    } else if (result.data.success && result.data.agents?.length > 0) {
      this.agents = result.data.agents.map(a => {
        const t = agentService.transformAgent(a);
        t.lastActive = agentService.relativeTime(t.lastActiveAt);
        return t;
      });
    } else {
      this.agents = [];
    }

    this._loading = false;
  }

  async _loadProviderLists() {
    try {
      const [llmData, sttData, ttsData, ctxData] = await Promise.all([
        llmService.listProviders(),
        speechService.listSttProviders(),
        speechService.listTtsProviders(),
        llmService.listContextualProviders(),
      ]);
      if (llmData.success) this._llmProviders = llmData.providers || [];
      if (sttData.success) this._sttProviders = sttData.providers || [];
      if (ttsData.success) this._ttsProviders = ttsData.providers || [];
      if (ctxData.success) this._contextualLlmProviders = ctxData.providers || [];
    } catch (err) {
      console.error('[Agents] Failed to load provider lists:', err);
    }
  }

  /* ─── Actions ─── */

  _createAgent() {
    this._createName = '';
    this._showCreateModal = true;
  }

  async _confirmCreateAgent() {
    const name = this._createName?.trim();
    if (!name) return;
    this._showCreateModal = false;
    try {
      const data = await agentService.createAgent({ name });
      if (data.success) {
        this._loadAgents();
        this._showToast(`Agent "${name}" created`);
      } else {
        this._showToast('Create failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      this._showToast('Create failed: ' + err.message);
    }
  }

  /* ─── Event Handlers (from sub-components) ─── */

  _onSelectAgent(e) {
    this.selectedAgent = e.detail.agent;
  }

  _onBack() {
    this.selectedAgent = null;
  }

  _onAgentUpdated(e) {
    const updated = e.detail.agent;
    this.agents = this.agents.map(a => a.id === updated.id ? updated : a);
    this.selectedAgent = updated;
  }

  _onAgentDeleted(e) {
    this.selectedAgent = null;
    this._loadAgents();
  }

  _onSearch(e) {
    this._searchQuery = e.detail.query;
    this._loadAgents();
  }

  _onFilter(e) {
    this._statusFilter = e.detail.status;
    this._loadAgents();
  }

  _onToast(e) {
    this._showToast(e.detail.message);
  }

  _showToast(message) {
    this._toastMessage = message;
    this._toastVisible = true;
    setTimeout(() => { this._toastVisible = false; }, 3000);
  }

  /* ─── Render ─── */

  render() {
    return html`
      ${iconStyles}
      ${this.selectedAgent ? html`
        <agent-detail
          .agent=${this.selectedAgent}
          .llmProviders=${this._llmProviders}
          .sttProviders=${this._sttProviders}
          .ttsProviders=${this._ttsProviders}
          .contextualLlmProviders=${this._contextualLlmProviders}
          @back=${this._onBack}
          @agent-updated=${this._onAgentUpdated}
          @agent-deleted=${this._onAgentDeleted}
          @toast=${this._onToast}
        ></agent-detail>
      ` : html`
        <agent-grid
          .agents=${this.agents}
          .loading=${this._loading}
          .searchQuery=${this._searchQuery}
          .statusFilter=${this._statusFilter}
          .ttsProviders=${this._ttsProviders}
          @select-agent=${this._onSelectAgent}
          @create-agent=${() => this._createAgent()}
          @search=${this._onSearch}
          @filter=${this._onFilter}
        ></agent-grid>
      `}
      <div class="toast ${this._toastVisible ? 'visible' : ''}">
        <i class="fa-sharp fa-light fa-check"></i>
        ${this._toastMessage || 'Saved successfully'}
      </div>

      <voice-modal ?open=${this._showCreateModal} title="Create Agent" width="440px"
        @modal-close=${() => { this._showCreateModal = false; }}>
        <div style="padding:var(--space-6, 24px);">
        <div class="form-group">
          <label class="form-label">Agent Name</label>
          <input
            class="form-input"
            type="text"
            placeholder="e.g. Front Desk Agent"
            .value=${this._createName}
            @input=${(e) => { this._createName = e.target.value; }}
            @keydown=${(e) => { if (e.key === 'Enter') this._confirmCreateAgent(); }}
          />
        </div>
        </div>
        <div slot="footer">
          <button class="btn btn-secondary" @click=${() => { this._showCreateModal = false; }}>Cancel</button>
          <button class="btn btn-primary" @click=${this._confirmCreateAgent} ?disabled=${!this._createName?.trim()}>Create</button>
        </div>
      </voice-modal>
    `;
  }
}

customElements.define('agent-page', AgentPage);
